import React from 'react';
import './StupidSystemDesigns.css';

const taskSchedulerDesignCode = String.raw`addTask(taskId, startTime, duration):
    if taskId already exists or duration <= 0:
        return failure

    task = new Task(taskId, startTime, duration)
    task.state = SCHEDULED

    taskMap[taskId] = task
    taskQueueService.addTask(task)
    return success

removeTask(taskId):
    if taskId does not exist:
        return failure

    task = taskMap[taskId]

    if task.state not in [SCHEDULED, READY]:
        return failure

    task.state = CANCELLED
    return success

addTask(task):
    scheduledQueue.push(task)

flushToExecutionQueue(currentTime):
    while scheduledQueue is not empty
          and scheduledQueue.top.startTime <= currentTime:
        task = scheduledQueue.pop()

        if task.state == CANCELLED:
            continue

        task.state = READY
        executionQueue.push(task)

executeNextTask(currentTime):
    flushToExecutionQueue(currentTime)

    while executionQueue is not empty:
        task = executionQueue.pop()

        if task.state == CANCELLED:
            continue

        task.state = RUNNING
        execute(task)
        task.state = COMPLETED
        return task

    return NONE`;

function TaskSchedulerDesign({ theme, onBackClick }) {
    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Task Scheduler LLD</h1>
                            <p className="system-designs-subtitle">
                                Schedule tasks by start time, then move eligible work into a ready-to-execute queue.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Requirements</h2>
                            <ul>
                                <li>Users can schedule tasks with a task ID, start time, and duration.</li>
                                <li>Tasks execute only after their scheduled start time has arrived.</li>
                                <li>Users can cancel tasks before execution begins.</li>
                                <li>Tasks execute in increasing order of scheduled start time.</li>
                                <li>The scheduler returns the next task eligible for execution.</li>
                                <li>Support checking and updating task states.</li>
                            </ul>
                            <p className="system-design-description">
                                <strong>Design decision:</strong> Use a min-heap to order scheduled tasks and a FIFO queue for tasks ready to execute.
                            </p>
                        </div>

                        <div className="design-section">
                            <h2>Core Entities</h2>
                            <ul>
                                <li>
                                    <strong>SchedulerService:</strong> Manages task records in <code>taskMap: Map&lt;TaskId, Task&gt;</code>
                                    and delegates queue operations to <code>TaskQueueService</code>. It provides
                                    {' '}<code>addTask(taskId, startTime, duration)</code>, <code>removeTask(taskId)</code>, and
                                    {' '}<code>executeNextTask(currentTime)</code>.
                                </li>
                                <li>
                                    <strong>Task:</strong> Stores <code>taskId</code>, <code>startTime</code>, <code>duration</code>,
                                    and <code>state</code>. States are SCHEDULED, READY, RUNNING, COMPLETED, and CANCELLED.
                                </li>
                                <li>
                                    <strong>TaskQueueService:</strong> Maintains <code>scheduledQueue: MinHeap&lt;Task&gt;</code>
                                    ordered by start time and <code>executionQueue: Queue&lt;Task&gt;</code> for ready tasks.
                                    It supports adding and removing tasks, flushing eligible tasks, and selecting the next task.
                                </li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Pseudocode</h2>
                            <pre className="design-code-block"><code>{taskSchedulerDesignCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <h2>Important Design Decisions</h2>
                            <ol>
                                <li><strong>Why use a min-heap?</strong> It efficiently retrieves the task with the earliest scheduled start time.</li>
                                <li><strong>Why use two queues?</strong> The heap stores future tasks; the FIFO queue stores tasks whose start times have arrived.</li>
                                <li><strong>Why pass currentTime?</strong> It makes the scheduler easy to test and simulate without relying directly on the system clock.</li>
                                <li>
                                    <strong>How does cancellation work?</strong> Only SCHEDULED or READY tasks can be cancelled.
                                    Cancelled heap entries may remain until popped; cancelled tasks are skipped when processing either queue.
                                </li>
                                <li><strong>What about identical start times?</strong> Add a sequence number as a secondary heap ordering key to preserve insertion order.</li>
                                <li><strong>What does executeNextTask return?</strong> The next eligible task, or NONE if no task is ready.</li>
                            </ol>
                        </div>

                        <div className="design-section">
                            <h2>Execution Model</h2>
                            <p className="system-design-description">
                                In this simplified design, execution is synchronous: the task moves to RUNNING, executes, then becomes COMPLETED.
                                In production, a worker or executor would generally run tasks asynchronously and mark them completed only after
                                execution finishes.
                            </p>
                            <p className="system-design-description">
                                <strong>Core design principle:</strong> SchedulerService manages task records and delegates queue operations
                                to TaskQueueService. The min-heap handles scheduling order, and the execution queue handles ready tasks.
                            </p>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        The task to come up with a witty footer for this page is scheduled. Whether it actually gets done is outside this scheduler’s jurisdiction. (you may have noticed there is no implementation for execute(task).)
                    </div>

                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default TaskSchedulerDesign;
